import { dateLocales } from '@/components/language-switcher'
import { Contact, User } from '@/types'
import {
  differenceInMinutes,
  eachHourOfInterval,
  format,
  intervalToDuration,
  isAfter,
} from 'date-fns'
import {
  isArray,
  isNull,
  isObject,
  isString,
  isUndefined,
  mapKeys,
  mapValues,
  snakeCase,
} from 'lodash-es'
import { i18n } from 'next-i18next'

const frEUR = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
})

export const formatPrice = (price: number | undefined) => {
  if (price === undefined) {
    return null
  }

  return frEUR.format(price)
}

export const formatName = (contact?: Contact | User) => {
  if (!contact) {
    return ''
  }

  if (contact.firstName && contact.lastName) {
    return `${contact.firstName} ${contact.lastName}`
  }

  if (contact.firstName) {
    return contact.firstName
  }

  if (contact.lastName) {
    return contact.lastName
  }

  return ''
}

export const formatDate = (
  date: string | Date | null,
  toFormat: string = 'HH:mm, dd MMM yyyy'
) => {
  if (!date) {
    return ''
  }

  const locale = i18n?.language ?? 'en'

  return format(date, toFormat, { locale: dateLocales[locale] })
}

export const formatDuration = (
  startDate: string | Date | null,
  endDate: string | Date | null
) => {
  if (!startDate || !endDate) return ''

  const totalMinutes = differenceInMinutes(endDate, startDate)
  if (totalMinutes <= 0) return '0m'

  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`)

  return parts.join(' ')
}

export const removeNullParams = (
  params: Record<string, any>
): URLSearchParams => {
  if (!params) {
    return new URLSearchParams()
  }

  Object.keys(params).forEach((key) => {
    const value = (params as any)[key]
    if (
      isNull(value) ||
      isUndefined(value) ||
      (isString(value) && value.length === 0)
    ) {
      delete (params as any)[key]
    }
  })

  return new URLSearchParams(params)
}

export function snakeCaseObject(obj: Record<string, any>): any {
  if (isArray(obj)) {
    return obj.map(snakeCaseObject)
  }

  if (isObject(obj)) {
    return mapKeys(mapValues(obj, snakeCaseObject), (value, key) =>
      snakeCase(key)
    )
  }

  return obj
}

export const getTimeZoneOffset = () => new Date().getTimezoneOffset() / -60

export const generateTimeSlots = (
  startTime: string,
  endTime: string,
  intervalHours: number
) => {
  const startDate = new Date(`2000-01-01T${startTime}`)
  const endDate = new Date(`2000-01-01T${endTime}`)

  if (isAfter(startDate, endDate)) {
    return []
  }

  const hours = eachHourOfInterval(
    {
      start: startDate,
      end: endDate,
    },
    {
      step: intervalHours,
    }
  )

  return hours.map((hour) => format(hour, 'HH:mm'))
}

export const formatUrl = (url: string) => {
  if (!url) {
    return ''
  }

  if (url.startsWith('http')) {
    return url
  }

  return `https://${url}`
}

export function formatDurationFromMinutes(totalMinutes: number) {
  const duration = intervalToDuration({
    start: 0,
    end: totalMinutes * 60 * 1000,
  })

  const parts = []
  if (duration.days) parts.push(`${duration.days}d`)
  if (duration.hours) parts.push(`${duration.hours}h`)
  if (duration.minutes) parts.push(`${duration.minutes}m`)

  return parts.length > 0 ? parts.join(' ') : '0m'
}
